import { TabContext, TabPanel } from '@mui/lab';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    ClickAwayListener,
    Container,
    SvgIcon,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { DateRangePicker } from 'date-range-picker-mui';
import dayjs from 'dayjs';
import { CalendarRange, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportCSVComponent from 'src/components/ExportCSVComponent';
import Page from 'src/components/Page';
import CalculationBreakdownDialog from 'src/components/CalculationBreakdownDialog';
import { websiteTitle } from 'src/config';
import useGetGrantedSubAccounts from 'src/hooks/useGetGrantedSubAccounts';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { makeStyles } from 'tss-react/mui';
import AuditReportTable from './AuditReportTable';
import AuditReportSummary from './AuditReportSummary';
import useAuth from 'src/hooks/useAuth';

const useStyles = makeStyles()((theme) => ({
    metalRoot: {
        [theme.breakpoints.up('md')]: {
            padding: `0 ${theme.spacing(4)} ${theme.spacing(0.5)}`,
        }
    },
    desktopTab: {
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        color: 'var(--accent-9-contrast) !important',
        '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'var(--color-surface-raised)'
        },
        boxShadow: '0 0 0 0 transparent',
        textTransform: 'none',
        padding: 'var(--spacing-md, 8px) var(--spacing-xl, 16px)',
        minHeight: '40px !important'
    },
    smallDeviceTab: {
        padding: 'var(--spacing-md, 8px) 0px'
    },
    selectedTab: {
        '& .Mui-selected': {
            backgroundColor: 'var(--Colors-Background-bg-active, #1F242F);',
        },
        alignItems: 'center !important',
        marginRight: 8,
        '& .MuiTab-root': {
            color: 'var(--accent-9-contrast)'
        }
    },
    dateDropdown: {
        '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'var(--color-surface-raised)'
        },
    },
    datePicker: {
        position: 'fixed',
        top: 230,
        right: 250,
        zIndex: 1001,
        border: '1px solid var(--Colors-Border-border-secondary, #242428)',
        borderRadius: 'var(--radius-xl, 12px)',
        '& .MuiBox-root': {
            height: 'auto !important',
            width: 'auto !important',
        },
        '& .MuiPaper-root': {
            backgroundColor: 'var(--neutral-2)',
            borderRadius: 'var(--radius-xl, 12px)'
        },
        '& .css-1g5dopa, .css-1efd2au, .css-m77go1': {
            backgroundColor: 'transparent'
        },
        '& .MuiTypography-root.MuiTypography-subtitle1': {
            fontSize: 16
        },
        '& .css-1efd2au Button, .css-m77go1 Button, .css-15lo3ef Button': {
            background: 'var(--Colors-Background-bg-brand-solid, #00D474) !important'
        },
        '& .MuiListItemButton-root': {
            color: 'var(--colors-text-text-secondary-700, #B2B2B8)',
        },
        '& .MuiButtonBase-root.MuiListItemButton-root': {
            color: 'var(--Colors-Text-text-secondary_hover, #EAECF0)',
            background: 'var(--neutral-2)',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.04)'
            }
        }
    },
    subAccountDropdown: {
        '& .MuiOutlinedInput-root': {
            padding: '1px 4px !important'
        },
        '& .MuiChip-root': {
            height: 30
        }
    },
    calculationInfo: {
        cursor: 'pointer',
        color: 'rgb(96, 165, 250)',
        '&:hover': {
            color: 'rgb(147 197 253 / var(--tw-text-opacity, 1));'
        },
    },
    redesignBtn: {
        borderRadius: 'var(--radius-xs, 4px) !important',
        background: 'var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00B161) !important',
        padding: '10px 14px !important',
        color: '#FFFFFF !important',
        textTransform: 'capitalize',
        fontWeight: 600,
        fontSize: 14,
        height: '40px !important',
        boxShadow: 'none',
        "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.26) !important",
            boxShadow: "none",
            backgroundColor: "rgba(255, 255, 255, 0.12) !important"
        },
        "& svg": {
            position: "relative",
            top: "2px",
            marginRight: "4px"
        },
        "& .arrowLeft, & .arrowRight": {
            transform: "translate(0px, 0px)",
            transition: "all 0.5s ease",
        },
        '&:hover, &.Mui-focusVisible': {
            cursor: "pointer",
            backgroundColor: 'var(--accent-5)',
            "& .arrowLeft, & .arrowRight": {
                transform: "translate(0px, 3px)",
                transition: "all 0.5s ease",
            }
        },
    },
}));



const TrustCenterDashboard = (props) => {
    const { t } = useTranslation();
    const { loggedInUser } = useAuth();
    const manageUsersPermissionOrAdmin =
        loggedInUser?.groups?.filter(group => !!group.userRole.manageUsers)?.length > 0 || loggedInUser.adminRole.adminRead;
    const { classes, cx } = useStyles();
    const theme = useTheme()
    const pageTitle = websiteTitle + " - " + t('trustCenter');
    const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
    const smallDevice = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedTab, setSelectedTab] = useState('audit-report');
    const { selectedGroup } = useSelectedGroup();
    const {
        data: subAccounts,
        isLoading: subAccountsLoading
    } = useGetGrantedSubAccounts(selectedGroup.map(g => g.group.name));

    const [selectedSubAccounts, setSelectedSubAccounts] = useState([
        'All Sub-Accounts'
    ]);
    const [startDate, setStartDate] = useState(
        dayjs(dayjs().subtract(30, 'days').startOf('day').toDate()).format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs(dayjs().toDate()).format('YYYY-MM-DD'));
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const dateRanges = [
        { label: 'Today', startDate: dayjs().startOf('day').toDate(), endDate: dayjs().toDate() },
        { label: 'Yesterday', startDate: dayjs().subtract(1, 'day').startOf('day').toDate(), endDate: dayjs().subtract(1, 'day').endOf('day').toDate() },
        { label: 'Last 7 Days', startDate: dayjs().subtract(7, 'days').startOf('day').toDate(), endDate: dayjs().toDate() },
        { label: 'Month to Date', startDate: dayjs().startOf('month').toDate(), endDate: dayjs().toDate() },
        { label: 'Last 30 Days', startDate: dayjs().subtract(30, 'days').startOf('day').toDate(), endDate: dayjs().toDate() },
        { label: 'Last 90 Days', startDate: dayjs().subtract(90, 'days').startOf('day').toDate(), endDate: dayjs().toDate() },
        { label: 'Year to Date', startDate: dayjs().startOf('year').toDate(), endDate: dayjs().toDate() },
        { label: 'Last 365 Days', startDate: dayjs().subtract(365, 'days').startOf('day').toDate(), endDate: dayjs().toDate() },
    ];
    const [formattedDateRange, setFormattedDateRange] = useState('Last 30 Days');
    const [tableExportData, setTableExportData] = useState([]);
    const [summaryStatsExport, setSummaryStatsExport] = useState([]);
    const [summaryWalletsExport, setSummaryWalletExport] = useState([]);
    const [methodologyDialogOpen, setMethodologyDialogOpen] = useState(false);
    const [showMinDateInfo, setShowMinDateInfo] = useState(false);
    const MIN_GET_BLOCK_COUNTS_DATE = '07/01/2020';

    const handleTabChange = (event, value) => {
        setSelectedTab(value);
    };

    const handleToggleDatePicker = event => {
        if (anchorEl == null) {
            setAnchorEl(event.currentTarget);
            setDatePickerOpen(true);
        } else {
            setAnchorEl(null);
            setDatePickerOpen(false);
        }
    };

    const handleDateChange = event => {
        setFormattedDateRange(formatDateRange(event.startDate, event.endDate))
        setStartDate(dayjs(event.startDate).format('YYYY-MM-DD'));
        setEndDate(dayjs(event.endDate).format('YYYY-MM-DD'));
        setAnchorEl(null);
        setDatePickerOpen(false);
    };

    const handleSubAccountChange = (event, accounts) => {
        if (
            !selectedSubAccounts.includes('All Sub-Accounts') &&
            accounts.includes('All Sub-Accounts')
        ) {
            setSelectedSubAccounts(['All Sub-Accounts']);
        } else if (
            !accounts.length ||
            (selectedSubAccounts.includes('All Sub-Accounts') &&
                accounts.includes('All Sub-Accounts'))
        ) {
            setSelectedSubAccounts([]);
        } else if (!!accounts.length) {
            setSelectedSubAccounts([
                ...accounts.filter(acct => acct !== 'All Sub-Accounts')
            ]);
        }
    };

    const formatDateRange = (startDate, endDate) => {
        const datesMatchExistingDateRange = dateRanges.filter(range => (dayjs(range.startDate).toDate().getTime() === dayjs(startDate).toDate().getTime()) &&
            (dayjs(range.endDate).toDate().getTime() === dayjs(endDate).toDate().getTime()));
        if (datesMatchExistingDateRange.length > 0) {
            return datesMatchExistingDateRange[0].label;
        } else if (!datesMatchExistingDateRange.length && dayjs(startDate).get('year') === dayjs(endDate).get('year')) {
            return `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format('MMM DD, YYYY')}`;
        } else {
            return `${dayjs(startDate).format('MMM DD, YYYY')} - ${dayjs(endDate).format('MMM DD, YYYY')}`;
        }
    }

    const getExportDataAndFileInfo = () => {
        return [
            { fileName: 'Audit Report - Daily Stats - ', data: tableExportData },
            { fileName: 'Audit Report - Summary - ', data: summaryStatsExport },
            { fileName: 'Audit Report - Wallets - ', data: summaryWalletsExport }
        ];
    }

    return (
        <Page className={classes.metalRoot} title={pageTitle}>
            <Container
                maxWidth="xxl"
                style={mediumDevice ? { padding: '0 16px' } : {}}
            >
                <TabContext value={selectedTab}>
                    <Card
                        style={{
                            backgroundColor: 'var(--uishell-color-surface)'
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            p={2}
                        >
                            <Typography variant={smallDevice ? 'h5' : 'h4'} style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                                {"Audit Report"}
                            </Typography>
                            {selectedTab === 'audit-report' ? ( // this will always be the case until SOC is done for zec
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    alignItems="center"
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={handleToggleDatePicker}
                                        style={{
                                            height: 40,
                                            color:
                                                'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)',
                                            border: datePickerOpen
                                                ? '1px solid var(--accent-9)'
                                                : '1px solid var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)',
                                            zIndex: 1000,
                                            textTransform: 'capitalize',
                                            background: 'var(--neutral-2)',
                                            whiteSpace: 'nowrap'
                                        }}
                                        className={classes.dateDropdown}
                                        startIcon={
                                            <CalendarRange size={23} style={{ marginBottom: 2 }} />
                                        }
                                        endIcon={datePickerOpen ? <ChevronUp /> : <ChevronDown />}
                                    >
                                        {formattedDateRange}
                                    </Button>
                                    <DateRangePicker
                                        open={datePickerOpen}
                                        onChange={handleDateChange}
                                        toggle={handleToggleDatePicker}
                                        initialDateRange={{ startDate, endDate }}
                                        minDate={dayjs(MIN_GET_BLOCK_COUNTS_DATE).toDate()}
                                        maxDate={new Date()}
                                        definedRanges={dateRanges}
                                        wrapperClassName={classes.datePicker}
                                    />
                                    <ClickAwayListener onClickAway={() => setShowMinDateInfo(false)}>
                                        <Tooltip
                                            onClick={() => setShowMinDateInfo(!showMinDateInfo)}
                                            onClose={() => setShowMinDateInfo(false)}
                                            open={showMinDateInfo}
                                            disableFocusListener
                                            disableHoverListener
                                            title={<Typography color="var(--color-text)" variant='h6' pt={1} pl={2} pb={0.5}>
                                                July 1, 2020 is the minimum date to retrieve audit report data.
                                            </Typography>}
                                            arrow
                                        >
                                            <Info
                                                size={20}
                                                style={{ marginLeft: theme.spacing(1), marginRight: theme.spacing(3), cursor: 'pointer' }}
                                            />
                                        </Tooltip>
                                    </ClickAwayListener>
                                    <Autocomplete
                                        multiple={true}
                                        disableCloseOnSelect
                                        loading={subAccountsLoading}
                                        disabled={subAccountsLoading}
                                        sx={{
                                            marginRight: '16px',
                                            border:
                                                'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)'
                                        }}
                                        getOptionDisabled={option =>
                                            selectedSubAccounts.includes('All Sub-Accounts') &&
                                            option !== 'All Sub-Accounts'
                                        }
                                        onChange={handleSubAccountChange}
                                        options={['All Sub-Accounts', ...subAccounts.sort()]}
                                        defaultValue={['All Sub-Accounts']}
                                        value={selectedSubAccounts}
                                        popupIcon={
                                            <ChevronDown
                                                style={{
                                                    color:
                                                        'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                                                }}
                                            />
                                        }
                                        slotProps={{
                                            popper: {
                                                style: {
                                                    width: 400
                                                },
                                                placement: 'bottom-start'
                                            }
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                style={{
                                                    width: smallDevice ? 220 : 250,
                                                    background: 'var(--neutral-2)'
                                                }}
                                                variant="outlined"
                                                className={classes.subAccountDropdown}
                                                {...params}
                                                placeholder={
                                                    !selectedSubAccounts.length
                                                        ? 'Select Sub-Accounts'
                                                        : ''
                                                }
                                            />
                                        )}
                                        renderOption={(props, option, { selected }) => {
                                            return (
                                                <li key={option.key} {...props}>
                                                    <Checkbox
                                                        style={{
                                                            marginRight: 8,
                                                            paddingTop: 5,
                                                            paddingBottom: 5
                                                        }}
                                                        checked={
                                                            selected ||
                                                            (selectedSubAccounts.length &&
                                                                selectedSubAccounts.includes(
                                                                    'All Sub-Accounts'
                                                                ))
                                                        }
                                                        disabled={
                                                            selectedSubAccounts.length &&
                                                            selectedSubAccounts.includes(
                                                                'All Sub-Accounts'
                                                            ) &&
                                                            option !== 'All Sub-Accounts'
                                                        }
                                                    />
                                                    <Typography>{option}</Typography>
                                                </li>
                                            );
                                        }}
                                        renderTags={(value, getTagProps) => {
                                            if (value[0] === 'All Sub-Accounts') {
                                                return (
                                                    <Chip
                                                        style={{
                                                            border: '1px solid #364152',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            color:
                                                                'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                                                        }}
                                                        label={'All Sub-Accounts'}
                                                    />
                                                );
                                            } else {
                                                return (
                                                    <Chip
                                                        style={{
                                                            border: '1px solid #364152',
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            color:
                                                                'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                                                        }}
                                                        label={`${selectedSubAccounts.length} ${value.length === 1
                                                            ? 'Sub-Account'
                                                            : 'Sub-Accounts'
                                                            }`}
                                                    />
                                                );
                                            }
                                        }}
                                    />
                                    <ExportCSVComponent
                                        fileName={`Audit Report - `}
                                        variant={'contained'}
                                        disableExport={!selectedSubAccounts.length || (!summaryStatsExport.length && selectedTab !== 'audit-report')}
                                        groupReport={true}
                                        getCSVData={getExportDataAndFileInfo}
                                        singleDate={dayjs(startDate).toDate() === dayjs(endDate).toDate()}
                                        startDate={dayjs(startDate).format('YYYY-MM-DD')}
                                        endDate={dayjs(endDate).format('YYYY-MM-DD')}
                                        pdfOption={false}
                                        multiFileExport={true}
                                    />
                                </Box>
                            ) : <>
                            </>
                            }
                        </Box>
                    </Card>
                    <Box>
                        <TabPanel value="audit-report" style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <Typography variant="body2" sx={{ mb: '1.5rem', color: 'var(--color-text)', padding: '0rem 1rem' }}>
                                Learn more about the data in the table below <span
                                    className={classes.calculationInfo}
                                    onClick={() => setMethodologyDialogOpen(true)}>here</span>.
                            </Typography>
                            <AuditReportSummary
                                startDate={startDate}
                                endDate={endDate}
                                subAccounts={selectedSubAccounts.includes('All Sub-Accounts') ? subAccounts : selectedSubAccounts}
                                saveSummaryStatsExport={setSummaryStatsExport}
                                saveSummaryWalletsExport={setSummaryWalletExport}
                            />
                            <AuditReportTable
                                startDate={startDate}
                                endDate={endDate}
                                subAccounts={selectedSubAccounts.includes('All Sub-Accounts') ? subAccounts : selectedSubAccounts}
                                saveExportData={setTableExportData}
                            />
                        </TabPanel>
                    </Box>
                </TabContext>
            </Container>

            <CalculationBreakdownDialog
                open={methodologyDialogOpen}
                onClose={() => setMethodologyDialogOpen(false)}
            />
        </Page>
    );
};

export default TrustCenterDashboard;
